import type { AvatarParts, AvatarType, OutfitId } from "@aproape/shared";

export interface SessionUser { id: string; isGuest: boolean; displayName: string; email: string; avatarType: AvatarType; avatarParts: AvatarParts; createdAt: number }
export type RelationshipKind = "friend" | "boyfriend" | "girlfriend";
const API = import.meta.env.VITE_API_URL || "";

type AuthResponse = { token: string; user: SessionUser };

const errorMessages: Record<string, string> = {
  EMAIL_IN_USE: "Există deja un cont cu acest email. Încearcă să te conectezi.",
  INVALID_CREDENTIALS: "Emailul sau parola nu sunt corecte.",
  INVALID_DISPLAY_NAME: "Numele avatarului trebuie să aibă între 3 și 20 de caractere.",
  INVALID_EMAIL: "Introdu o adresă de email validă.",
  INVALID_PASSWORD: "Parola trebuie să aibă cel puțin 8 caractere.",
  GUEST_LIMIT_REACHED: "Sunt prea mulți vizitatori acum. Încearcă din nou peste puțin timp.",
};

async function authRequest(path: string, body: object): Promise<AuthResponse> {
  const response = await fetch(`${API}${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(errorMessages[payload.error ?? ""] ?? "Nu am putut continua. Încearcă din nou.");
  }
  return response.json();
}

export function registerAccount(displayName: string, email: string, password: string, avatarType: AvatarType, outfit: OutfitId): Promise<AuthResponse> {
  return authRequest("/api/register", { displayName, email, password, avatarType, outfit });
}

export function loginAccount(email: string, password: string): Promise<AuthResponse> {
  return authRequest("/api/login", { email, password });
}

export function createGuestSession(displayName: string, avatarType: AvatarType, outfit: OutfitId): Promise<AuthResponse> {
  return authRequest("/api/guest", { displayName, avatarType, outfit });
}

export async function restoreSession(token: string): Promise<SessionUser | null> {
  const response = await fetch(`${API}/api/session`, { headers: { Authorization: `Bearer ${token}` } });
  return response.ok ? (await response.json()).user : null;
}

export async function revokeSession(token: string): Promise<void> {
  await fetch(`${API}/api/logout`, { method: "POST", headers: { Authorization: `Bearer ${token}` } }).catch(() => undefined);
}

export async function updateAvatarSkin(token: string, outfit: OutfitId): Promise<SessionUser> {
  const response = await fetch(`${API}/api/avatar`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ outfit }),
  });
  if (!response.ok) throw new Error("Nu am putut salva ținuta. Încearcă din nou.");
  return (await response.json()).user;
}

export async function updateProfileName(token: string, displayName: string): Promise<SessionUser> {
  const response = await fetch(`${API}/api/profile`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ displayName }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(errorMessages[payload.error ?? ""] ?? "Nu am putut salva numele. Încearcă din nou.");
  }
  return (await response.json()).user;
}

async function relationshipRequest(token: string, targetUserId: string, kind?: RelationshipKind | null): Promise<RelationshipKind | null> {
  const response = await fetch(`${API}/api/relationships/${encodeURIComponent(targetUserId)}`, {
    method: kind === undefined ? "GET" : "POST",
    headers: { ...(kind === undefined ? {} : { "Content-Type": "application/json" }), Authorization: `Bearer ${token}` },
    ...(kind === undefined ? {} : { body: JSON.stringify({ kind }) }),
  });
  if (!response.ok) throw new Error("Nu am putut salva relația. Încearcă din nou.");
  const payload = await response.json() as { relationship: { kind: RelationshipKind } | null };
  return payload.relationship?.kind ?? null;
}

export function getRelationship(token: string, targetUserId: string): Promise<RelationshipKind | null> {
  return relationshipRequest(token, targetUserId);
}

export function setRelationship(token: string, targetUserId: string, kind: RelationshipKind | null): Promise<RelationshipKind | null> {
  return relationshipRequest(token, targetUserId, kind);
}
